import {
  normalizeAgentPresentation,
  type AgentPresentation,
  type PresentationLifecycle,
} from "@/lib/agent-presentation-contract";
import type { PresentationAppReason } from "@/lib/agent-presentation-types";
import type { WsMessage } from "@/api/websocket";
import {
  acknowledgePresentationCompletion,
  cancelPresentationDelivery,
  failPresentationStep,
} from "@/lib/agent-presentation-ack";
import {
  defaultPresentationOperations,
  type PresentationOperations,
} from "@/lib/agent-presentation-operations";
import { mergePresentationReceipts } from "./presentation-local-receipts";
import { performPresentationStep } from "./presentation-step-runner";
import {
  componentComplete,
  reportStepProgress,
  awaitPresentationStep,
} from "./presentation-step-progress";
import { recordPresentationCompletion } from "./presentation-completion";
import { PRESENTATION_FINISHED } from "./presentation-finished";
import { runGuidedStep } from "./presentation-guided-step";
import { dismissCompletedGuideStep } from "./presentation-guide-cleanup";
import { tracePresentationSequence } from "./agent-presentation-delivery-trace";
import { tracePresentationCancellation } from "./agent-presentation-delivery-trace";
import { useProjectStore } from "@/stores/project-store";
import { isDisplayWindow } from "@/desktop/setup-window-surface";
export type { PresentationOperations } from "@/lib/agent-presentation-operations";

/** Briefly hold the final pointer after delivery. Completed annotations stay
 *  available through their existing dismissal and replacement lifecycle. */
export const PRESENTATION_COMPLETION_GRACE_MS = 1_500;

export interface PresentationRun {
  presentation: AgentPresentation;
  abortController: AbortController;
  lifecycle: PresentationLifecycle;
  stepIndex: number;
  usedCursor: boolean;
  retainsCompletedAnnotation: boolean;
  cleaned: boolean;
  finishGrace?: () => void;
}

export type PresentationDecision = "accepted" | "duplicate" | "rejected";

export interface PresentationVerdict {
  decision: PresentationDecision;
  reason?: PresentationAppReason;
}

export interface PresentationSnapshot {
  presentationId: string;
  executionId?: string;
  lifecycle: PresentationLifecycle;
  stepIndex: number;
}

export class AgentPresentationController {
  private active: PresentationRun | null = null;
  private actionSequence = 0;
  private readonly queue: PresentationRun[] = [];
  private readonly highestEpochByExecution = new Map<string, number>();
  private readonly runtimeSessionByExecution = new Map<string, string>();
  private readonly acceptedPresentationIds = new Set<string>();

  constructor(
    private readonly operations: PresentationOperations = defaultPresentationOperations,
  ) {}

  getSnapshot(): PresentationSnapshot | null {
    const run = this.active;
    return run
      ? {
          presentationId: run.presentation.presentationId,
          executionId: run.presentation.executionId,
          lifecycle: run.lifecycle,
          stepIndex: run.stepIndex,
        }
      : null;
  }

  bindExecutionSession(executionId: string, runtimeSessionId?: string): void {
    if (!runtimeSessionId) return;
    const previous = this.runtimeSessionByExecution.get(executionId);
    this.runtimeSessionByExecution.set(executionId, runtimeSessionId);
    if (previous && previous !== runtimeSessionId) {
      this.cancelExecution(executionId, "runtime_session_changed");
    }
  }

  present(presentation: AgentPresentation): boolean {
    return this.admit(presentation).decision === "accepted";
  }

  /**
   * The companion delivers only this user's runs, so a presentation shows
   * wherever the user is, whatever project its run or its frames belong to.
   * It is scoped to the viewed project, so its pointer, marks and clears
   * belong to the screen the user sees; frames are found by id in their own
   * project, and one of a project that is not on screen opens in Focus.
   */
  admit(incoming: AgentPresentation): PresentationVerdict {
    const presentation = inViewedProject(
      incoming,
      useProjectStore.getState().currentProject?.id,
    );
    const verdict = this.acceptance(presentation);
    if (verdict.decision !== "accepted") return verdict;
    const run: PresentationRun = {
      presentation: mergePresentationReceipts(presentation),
      abortController: new AbortController(),
      lifecycle: "queued",
      stepIndex: -1,
      usedCursor: false,
      retainsCompletedAnnotation: false,
      cleaned: false,
    };
    const first = presentation.steps[0];
    if (first) void reportStepProgress(presentation, first, "step", "waiting");
    this.queue.push(run);
    this.active?.finishGrace?.();
    this.startNext();
    return verdict;
  }

  private startNext(): void {
    if (this.active) return;
    const run = this.queue.shift();
    if (!run) return;
    this.active = run;
    void this.execute(run).finally(() => this.startNext());
  }

  private cancelQueued(
    matches: (presentation: AgentPresentation) => boolean,
    reason?: PresentationAppReason,
  ): void {
    for (let index = this.queue.length - 1; index >= 0; index--) {
      const run = this.queue[index];
      if (!run || !matches(run.presentation)) continue;
      this.queue.splice(index, 1);
      run.abortController.abort();
      cancelPresentationDelivery(run.presentation, reason);
    }
  }

  cancelExecution(executionId: string, reason?: PresentationAppReason): void {
    this.cancelQueued((p) => p.executionId === executionId, reason);
    if (this.active?.presentation.executionId === executionId) {
      this.cancelActive(reason ?? "execution_cancelled", reason);
    }
  }

  cancelSession(sessionId: string): void {
    this.cancelQueued(
      (p) => p.runtimeSessionId === sessionId || p.cliSessionId === sessionId,
    );
    const presentation = this.active?.presentation;
    if (
      presentation?.runtimeSessionId === sessionId ||
      presentation?.cliSessionId === sessionId
    ) {
      this.cancelActive("session_cancelled");
    }
  }

  /** Without a reason, the user stopped it. */
  cancelAll(reason?: PresentationAppReason): void {
    this.cancelQueued(() => true, reason);
    this.cancelActive(reason, reason);
  }

  /** The view moved to `projectId`: presentations go on there. */
  followViewedProject(projectId?: string): void {
    for (const run of this.active ? [this.active, ...this.queue] : this.queue)
      run.presentation = inViewedProject(run.presentation, projectId);
    if (this.active) this.operations.followVisuals?.(this.active.presentation);
  }

  cancelFailedDelivery(
    presentationId: string,
    executionId?: string,
    playbackAttempt?: number,
  ): void {
    const matches = (p: AgentPresentation) =>
      p.presentationId === presentationId &&
      (executionId == null || p.executionId === executionId) &&
      (playbackAttempt == null || (p.playbackAttempt ?? 1) === playbackAttempt);
    for (let index = this.queue.length - 1; index >= 0; index--) {
      const queued = this.queue[index];
      if (queued && matches(queued.presentation)) {
        this.queue.splice(index, 1);
        queued.abortController.abort();
      }
    }
    const run = this.active;
    if (!run || !matches(run.presentation)) return;
    run.lifecycle = "cancelled";
    run.abortController.abort();
    // Keep confirmed visuals available for a same-sequence recovery.
    if (this.active === run) this.active = null;
  }

  private acceptance(presentation: AgentPresentation): PresentationVerdict {
    const presentationKey = `${presentation.executionId ?? ""}\0${presentation.presentationId}\0${presentation.playbackAttempt ?? 1}`;
    if (this.acceptedPresentationIds.has(presentationKey)) {
      return { decision: "duplicate" };
    }
    const executionId = presentation.executionId;
    if (executionId) {
      const boundSession = this.runtimeSessionByExecution.get(executionId);
      if (
        boundSession &&
        presentation.runtimeSessionId &&
        boundSession !== presentation.runtimeSessionId
      ) {
        return { decision: "rejected", reason: "runtime_session_changed" };
      }
      if (presentation.enforceMonotonicEpoch && !presentation.resumeReceipt) {
        const highestEpoch = this.highestEpochByExecution.get(executionId);
        if (
          highestEpoch !== undefined &&
          presentation.turnEpoch <= highestEpoch
        ) {
          return { decision: "rejected", reason: "superseded_turn" };
        }
        this.highestEpochByExecution.set(executionId, presentation.turnEpoch);
      }
    }
    this.acceptedPresentationIds.add(presentationKey);
    while (this.acceptedPresentationIds.size > 128) {
      const oldest = this.acceptedPresentationIds.values().next().value;
      if (typeof oldest !== "string") break;
      this.acceptedPresentationIds.delete(oldest);
    }
    return { decision: "accepted" };
  }

  private cancelActive(
    trace = "cancel_all",
    reason?: PresentationAppReason,
  ): void {
    const run = this.active;
    if (!run) return;
    const wasCompleted = run.lifecycle === "completed";
    if (!wasCompleted) tracePresentationCancellation(run.presentation, trace);
    run.lifecycle = "cancelled";
    run.abortController.abort();
    if (!wasCompleted) cancelPresentationDelivery(run.presentation, reason);
    this.cleanup(run);
    if (this.active === run) this.active = null;
  }

  private cleanup(run: PresentationRun): void {
    if (run.cleaned) return;
    run.cleaned = true;
    if (run.usedCursor) this.operations.clearVisuals(run.presentation);
    // Frames belong to the user once shown. Aborting the run cancels queued
    // reveals; only explicit frame-clear steps or user controls close content.
  }

  private async wait(durationMs: number, run: PresentationRun): Promise<void> {
    if (durationMs <= 0) return;
    await this.operations.delay(durationMs, run.abortController.signal);
  }

  private async execute(run: PresentationRun): Promise<void> {
    try {
      for (const step of run.presentation.steps) {
        if (
          componentComplete(
            run.presentation.resumeReceipt?.find(
              (item) => item.step_id === step.step_id,
            )?.speech,
          )
        )
          continue;
        this.operations.prepare?.(
          run.presentation,
          step,
          run.abortController.signal,
        );
      }
      for (let index = 0; index < run.presentation.steps.length; index += 1) {
        if (run.abortController.signal.aborted || this.active !== run) return;
        const step = run.presentation.steps[index];
        if (!step) continue;
        if (
          componentComplete(
            run.presentation.resumeReceipt?.find(
              (item) => item.step_id === step.step_id,
            )?.step,
          )
        )
          continue;
        run.stepIndex = index;
        run.lifecycle = "rendering";
        void reportStepProgress(run.presentation, step, "step", "started");
        const guided =
          run.presentation.mode !== "operate" &&
          run.presentation.steps.some(
            (item) => "user_action" in item && item.user_action,
          );
        const successful = guided
          ? await runGuidedStep(
              run.presentation,
              step,
              index,
              run.abortController.signal,
              (signal, onVisible, replayCount) =>
                performPresentationStep(
                  run,
                  step,
                  signal,
                  onVisible,
                  this.operations,
                  () => ++this.actionSequence,
                  replayCount,
                ),
              () => this.cancelAll(),
            )
          : await performPresentationStep(
              run,
              step,
              run.abortController.signal,
              () => {},
              this.operations,
              () => ++this.actionSequence,
            );
        if (!successful || run.abortController.signal.aborted) {
          this.stop(run);
          return;
        }
        if (
          !(await reportStepProgress(
            run.presentation,
            step,
            "step",
            "finished",
          )) ||
          !(await awaitPresentationStep(run.presentation, step))
        ) {
          this.stop(run);
          return;
        }
        const completed = await acknowledgePresentationCompletion(
          run.presentation,
          step,
          run.abortController.signal,
        );
        if (!completed) {
          this.stop(run);
          return;
        }
        if (guided && !run.abortController.signal.aborted)
          dismissCompletedGuideStep(
            run.presentation,
            step,
            this.actionSequence,
          );
      }
      if (this.active === run) {
        run.lifecycle = "completed";
        recordPresentationCompletion(run.presentation);
        // Delivered frames stay open independently of later cursor guidance.
        // Explicit clear steps and user Focus controls own their dismissal.
        if (run.usedCursor) {
          if (this.queue.length === 0) {
            await Promise.race([
              this.wait(PRESENTATION_COMPLETION_GRACE_MS, run),
              new Promise<void>((resolve) => {
                run.finishGrace = resolve;
              }),
            ]);
            run.finishGrace = undefined;
          }
          if (run.abortController.signal.aborted || this.active !== run) return;
          if (run.retainsCompletedAnnotation) {
            this.operations.finishVisuals(run.presentation);
          } else {
            this.operations.clearVisuals(
              run.presentation,
              "presentation",
              true,
            );
          }
          run.usedCursor = false;
        }
        if (this.active === run) this.active = null;
        run.abortController.abort(PRESENTATION_FINISHED);
      }
    } catch {
      if (!run.abortController.signal.aborted) {
        const step = run.presentation.steps[run.stepIndex];
        if (step) {
          await failPresentationStep(
            run.presentation,
            step,
            "render_operation_threw",
          );
        }
        this.stop(run);
      }
    }
  }

  private stop(run: PresentationRun): void {
    run.lifecycle = "cancelled";
    run.abortController.abort();
    // Failure recovery reuses the painted visuals; explicit Stop owns cleanup.
    if (this.active === run) this.active = null;
  }
}

/** The presentation as one of the viewed project (none viewed: as it came). */
function inViewedProject(
  presentation: AgentPresentation,
  projectId?: string,
): AgentPresentation {
  return !projectId || presentation.projectId === projectId
    ? presentation
    : { ...presentation, projectId };
}

export const agentPresentationController = new AgentPresentationController();

let presentationProjectId = useProjectStore.getState().currentProject?.id;
useProjectStore.subscribe((state) => {
  const nextProjectId = state.currentProject?.id;
  if (nextProjectId === presentationProjectId) return;
  presentationProjectId = nextProjectId;
  agentPresentationController.followViewedProject(nextProjectId);
});

if (typeof window !== "undefined") {
  window.addEventListener("denker:presentation-stop", () =>
    agentPresentationController.cancelAll(),
  );
  window.addEventListener("pagehide", () =>
    agentPresentationController.cancelAll("app_window_closed"),
  );
  window.addEventListener("denker:presentation-delivery-failed", (event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!detail || typeof detail !== "object") return;
    const failure = detail as Record<string, unknown>;
    if (typeof failure.presentationId !== "string") return;
    agentPresentationController.cancelFailedDelivery(
      failure.presentationId,
      typeof failure.executionId === "string" ? failure.executionId : undefined,
      typeof failure.playbackAttempt === "number"
        ? failure.playbackAttempt
        : undefined,
    );
  });
}

export function applyAgentPresentationFrame(msg: WsMessage): boolean {
  const data = msg.data as Record<string, unknown>;
  const presentation = normalizeAgentPresentation(msg.type, data);
  if (!presentation) {
    if (msg.type === "canvas.presentation_sequence")
      tracePresentationSequence(data, "invalid");
    return false;
  }
  // Played once, by the display's own window (never the setup window there).
  if (!isDisplayWindow()) return true;
  const { decision, reason } = agentPresentationController.admit(presentation);
  tracePresentationSequence(data, decision, reason);
  if (decision === "rejected") {
    cancelPresentationDelivery(presentation, reason);
  }
  return true;
}

export function observeAgentPresentationLifecycle(
  msg: WsMessage,
  controller = agentPresentationController,
): void {
  if (msg.type === "execution.status") {
    const data = msg.data as Record<string, unknown>;
    const executionId =
      typeof data.execution_id === "string" ? data.execution_id : undefined;
    if (!executionId) return;
    controller.bindExecutionSession(
      executionId,
      typeof data.runtime_session_id === "string"
        ? data.runtime_session_id
        : undefined,
    );
    if (["cancelled", "failed", "paused"].includes(String(data.status))) {
      controller.cancelExecution(executionId);
    }
    return;
  }
}
